import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';

function shown(translate: (key: string) => string, key: string, fallback: string): string {
  const value = translate(key);
  return value === key ? fallback : value;
}

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Container maxWidth="lg" className="py-8 px-8">
        <Typography 
          variant="h4" 
          component="h1" 
          className="mb-6 text-left text-black"
        >
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" className="mb-8 text-left text-black">
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box className="space-y-6">
          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              1. Introduction
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              SonicMakers Ltd. ("we", "our", or "us") is committed to protecting and respecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
              you visit our website https://sonicmakers.com (the "Site") or use our services.
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              This policy complies with the General Data Protection Regulation (GDPR) and other applicable 
              data protection laws. By using our Site, you consent to the data practices described in this policy.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              2. Information We Collect
            </Typography>
            
            <Typography variant="h6" component="h3" className="mb-3 text-black">
              2.1 Personal Information
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              We may collect personal information that you voluntarily provide to us when you:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>Create an account on our Site</li>
              <li>Make a purchase</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact us for support</li>
              <li>Participate in surveys or promotions</li>
            </ul>
            
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              This information may include:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>Name and contact information (email address)</li>
              <li>Account credentials (username, password hash)</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Purchase history and download records</li>
              <li>Communication preferences</li>
            </ul>

            <Typography variant="h6" component="h3" className="mb-3 text-black">
              2.2 Automatically Collected Information
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              When you visit our Site, we may automatically collect certain information about your device 
              and usage patterns, including:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>IP address and general location information</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on our Site</li>
              <li>Referring website addresses</li>
              <li>Device identifiers</li>
            </ul>

            <Typography variant="h6" component="h3" className="mb-3 text-black">
              2.3 Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our Site and hold 
              certain information. For detailed information about our cookie usage, please refer to our 
              Cookie Policy.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              3. Legal Basis for Processing (GDPR)
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Under GDPR, we process your personal data based on the following legal grounds:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Contract Performance:</strong> Processing necessary for account creation, order fulfillment, and customer support</li>
              <li><strong>Legitimate Interest:</strong> Website analytics, fraud prevention, and service improvement</li>
              <li><strong>Consent:</strong> Marketing communications, optional cookies, and newsletter subscriptions</li>
              <li><strong>Legal Obligation:</strong> Tax records, financial reporting, and dispute resolution</li>
            </ul>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              4. How We Use Your Information
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              We use the information we collect for the following purposes:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Service Provision:</strong> Create and manage your account, process orders, provide downloads</li>
              <li><strong>Customer Support:</strong> Respond to inquiries, resolve technical issues, provide assistance</li>
              <li><strong>Communication:</strong> Send transactional emails, account notifications, and optional marketing</li>
              <li><strong>Analytics:</strong> Understand user behavior, improve our website and services</li>
              <li><strong>Legal Compliance:</strong> Maintain records, prevent fraud, comply with legal obligations</li>
              <li><strong>Marketing:</strong> Send promotional content (only with your consent)</li>
            </ul>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              5. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information only in the following circumstances:
            </Typography>
            
            <Typography variant="h6" component="h3" className="mb-3 text-black">
              5.1 Service Providers
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Stripe:</strong> {shown(t, 'privacy.stripe_processor', 'Payment processor for plugin purchases (PCI DSS compliant)')}</li>
              <li><strong>DigitalOcean:</strong> Website hosting and file storage</li>
              <li><strong>MongoDB Atlas:</strong> Database services</li>
              <li><strong>Email Service:</strong> Transactional and marketing emails</li>
            </ul>

            <Typography variant="h6" component="h3" className="mb-3 text-black">
              5.2 Legal Requirements
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              We may disclose your information if required by law or in response to valid legal requests.
            </Typography>

            <Typography variant="h6" component="h3" className="mb-3 text-black">
              5.3 Business Transfers
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred 
              as part of that transaction.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              6. Data Retention
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined 
              in this Privacy Policy, unless a longer retention period is required by law:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Account Information:</strong> Until account deletion or 3 years of inactivity</li>
              <li><strong>Purchase Records:</strong> 7 years for tax and legal compliance</li>
              <li><strong>Marketing Data:</strong> Until consent withdrawal or 2 years of inactivity</li>
              <li><strong>Support Communications:</strong> 3 years for quality assurance</li>
              <li><strong>Analytics Data:</strong> 26 months (anonymized after 14 months)</li>
            </ul>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              7. Your Rights Under GDPR
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              If you are a resident of the European Economic Area (EEA), you have the following rights:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Right of Access:</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of data processing</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for consent-based processing</li>
            </ul>
            <Typography variant="body1" className="text-black leading-relaxed">
              To exercise these rights, please contact us at privacy@sonicmakers.com. We will respond to 
              your request within 30 days.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              8. Data Security
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>SSL/TLS encryption for data in transit</li>
              <li>Encrypted storage for sensitive data</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure payment processing through Stripe</li>
              <li>Regular backups and disaster recovery procedures</li>
            </ul>
            <Typography variant="body1" className="text-black leading-relaxed">
              However, no method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your information, we cannot guarantee absolute security.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              9. International Data Transfers
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of 
              residence. We ensure that such transfers comply with applicable data protection laws and 
              implement appropriate safeguards, including standard contractual clauses approved by the 
              European Commission.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              10. Children's Privacy
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              Our Site is not intended for children under 16 years of age. We do not knowingly collect 
              personal information from children under 16. If you are a parent or guardian and believe 
              your child has provided us with personal information, please contact us immediately.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              11. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. For 
              significant changes, we may also send you an email notification if you have provided your 
              email address.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              12. Contact Information
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              If you have any questions about this Privacy Policy or wish to exercise your rights, 
              please contact us:
            </Typography>
            <Box className="mt-4  bg-white ">
              <Typography variant="body1" className="text-black">
                <strong>SonicMakers Ltd.</strong><br />
                Data Protection Officer<br />
                Email: privacy@sonicmakers.com<br />
                Website: https://sonicmakers.com
              </Typography>
            </Box>
            <Typography variant="body1" className="mt-4 text-black leading-relaxed">
              You also have the right to lodge a complaint with a supervisory authority, in particular 
              in the Member State of your habitual residence, place of work, or place of the alleged 
              infringement if you believe that the processing of your personal data violates applicable law.
            </Typography>
          </section>
        </Box>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;